import { Checkbox } from '@mui/material';
import { ReactElement, useState } from 'react';

export interface PaloPositionSelection {
    /** Places in the whole document, not on the page. */
    selected: number[];
    isSelected: (index: number) => boolean;
    toggle: (index: number) => void;
    /** Picks all of them, or clears them when they are all picked already. */
    toggleAll: (indices: number[]) => void;
    clear: () => void;
    /** Front to back, which is the order an action has to undo. */
    sorted: () => number[];
}

/**
 * Which positions are picked out.
 *
 * "Es ist alles auswählbar" is what was asked of this table (Stefan Graf,
 * 2026-09-23), and it is how ERPNext's grid works: a box on every row, one in
 * the heading for the page, and the actions appear once something is picked.
 *
 * A position is held by its place in the document. Deleting or moving shifts
 * those places, so whatever acts on a selection clears it afterwards.
 */
export const usePaloPositionSelection = (): PaloPositionSelection => {
    const [selected, setSelected] = useState<number[]>([]);

    return {
        selected,
        isSelected: (index: number) => selected.includes(index),
        toggle: (index: number) =>
            setSelected(current =>
                current.includes(index) ? current.filter(entry => entry !== index) : [...current, index]
            ),
        toggleAll: (indices: number[]) =>
            setSelected(current => (indices.every(index => current.includes(index)) ? [] : indices)),
        clear: () => setSelected([]),
        sorted: () => [...selected].sort((left, right) => left - right),
    };
};

/** The box on a row. */
export const PaloPositionSelectionBox = ({
    selected,
    index,
    onChange,
}: {
    selected: boolean;
    index: number;
    onChange: () => void;
}): ReactElement => (
    <Checkbox
        size="small"
        checked={selected}
        onChange={onChange}
        inputProps={{ 'aria-label': `Position ${index + 1} auswählen` }}
        sx={{ padding: '2px' }}
    />
);

/** The box in the heading, for the page one is looking at. */
export const PaloPositionSelectionAllBox = ({
    selection,
    indices,
}: {
    selection: PaloPositionSelection;
    indices: number[];
}): ReactElement => {
    const all = indices.length > 0 && indices.every(index => selection.isSelected(index));
    const some = !all && indices.some(index => selection.isSelected(index));

    return (
        <Checkbox
            size="small"
            checked={all}
            indeterminate={some}
            onChange={() => selection.toggleAll(indices)}
            inputProps={{ 'aria-label': 'Alle Positionen auswählen' }}
            sx={{ padding: '2px' }}
        />
    );
};
